import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { MapPin, Clock, Lock, ArrowLeft, Play, Sparkles, CreditCard, RotateCcw, Trophy, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import RouteSpecs from "../components/RouteSpecs";
import { useAuth } from "../lib/auth";

export default function RouteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [route, setRoute] = useState(null);
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(false);
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    api.get(`/routes/${id}`).then((r) => setRoute(r.data)).catch((e) => {
      toast.error(e.response?.data?.detail || "Errore");
      navigate("/");
    });
    // Carica il progress SOLO se l'utente è loggato. I visitatori anonimi possono
    // vedere il dettaglio ma non hanno un progress.
    if (user) {
      api.get(`/routes/${id}/progress`).then((r) => setProgress(r.data)).catch(() => setProgress(null));
    } else {
      setProgress(null);
    }
  }, [id, navigate, user]);

  const requireLoginThen = (nextAction) => {
    if (!user) {
      toast.info("Accedi per giocare a questa caccia al tesoro");
      navigate("/login", { state: { from: `/routes/${id}` } });
      return true;
    }
    nextAction();
    return false;
  };

  const startRoute = async () => {
    if (requireLoginThen(() => {})) return;
    setBusy(true);
    try {
      await api.post(`/routes/${id}/start`);
      navigate(`/play/${id}`);
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error("Devi acquistare il percorso");
      } else {
        toast.error(e.response?.data?.detail || "Errore avvio");
      }
    } finally {
      setBusy(false);
    }
  };

  const restartRoute = async () => {
    if (requireLoginThen(() => {})) return;
    const msg = "Ricominciare questa caccia al tesoro?\n\n" +
                "⚠️ I punti che farai NON verranno aggiunti al tuo totale: " +
                "i punti già guadagnati restano salvati nel tuo profilo.";
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      await api.post(`/routes/${id}/restart`);
      toast.success("Caccia ricominciata!");
      navigate(`/play/${id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore restart");
    } finally {
      setBusy(false);
    }
  };

  const buyRoute = async () => {
    if (requireLoginThen(() => {})) return;
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        route_id: id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore pagamento");
      setBusy(false);
    }
  };

  if (!route) return <Skeleton className="h-96 rounded-3xl" />;

  const purchased = route.is_purchased;
  const needsPayment = route.price_eur > 0 && !purchased;

  return (
    <div className="space-y-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-ink-700 hover:text-ink-900 font-bold" data-testid="back-btn">
        <ArrowLeft size={20} /> Indietro
      </button>

      <Card className="card-treasure overflow-hidden p-0" data-testid="route-detail-card">
        <div className="relative h-56 sm:h-72">
          {route.image_url ? (
            <img src={route.image_url} alt={route.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-forest-hover flex items-center justify-center text-white/60 text-sm">
              Nessuna immagine
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <div className="flex gap-2 mb-2">
              {route.price_eur > 0 ? (
                <Badge className="bg-compass text-white rounded-full font-bold border-0">€{route.price_eur.toFixed(2)}</Badge>
              ) : (
                <Badge className="bg-forest text-white rounded-full font-bold border-0">Gratis</Badge>
              )}
              <Badge className="bg-white/95 text-ink-800 rounded-full font-bold border-0">
                <MapPin size={12} className="mr-1" /> {route.city}
              </Badge>
              <Badge className="bg-white/95 text-ink-800 rounded-full font-bold border-0">
                <Clock size={12} className="mr-1" /> {route.duration_min} min
              </Badge>
            </div>
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-white">{route.title}</h1>
          </div>
        </div>
        <div className="p-6 space-y-5">
          <div
            className="rich-description text-ink-700 leading-relaxed"
            data-testid="route-description"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(route.description || "", {
                ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "s", "a", "h1", "h2", "h3", "ul", "ol", "li", "blockquote", "hr", "code", "pre"],
                ALLOWED_ATTR: ["href", "target", "rel"],
              }),
            }}
          />

          <RouteSpecs
            specs={{
              contesto_urbano: route.spec_contesto_urbano,
              sfida_mentale: route.spec_sfida_mentale,
              movimento: route.spec_movimento,
              spirito_squadra: route.spec_spirito_squadra,
              min_age: route.spec_min_age,
            }}
          />

          <div data-testid="route-points-summary">
            <div className="text-xs uppercase tracking-widest text-ink-500 font-bold mb-2">
              Tappe del percorso
            </div>
            <div className="flex items-center gap-3 p-4 bg-parchment-dark rounded-2xl border border-outline">
              <div className="w-12 h-12 rounded-2xl bg-gold flex items-center justify-center font-display font-extrabold text-ink-900 text-xl shrink-0" data-testid="route-points-count">
                {route.points.length}
              </div>
              <div className="flex-1">
                <div className="font-display font-extrabold text-ink-900">
                  {route.points.length} tappe da scoprire
                </div>
                <div className="text-xs text-ink-500 mt-0.5">
                  I luoghi resteranno una sorpresa fino al momento giusto.
                </div>
              </div>
              <Lock size={18} className="text-ink-500 shrink-0"/>
            </div>
          </div>

          {progress?.status === "completed" && (
            <div className="p-3 rounded-2xl bg-forest/10 border border-forest/30 flex items-center gap-3 text-sm" data-testid="route-completed-banner">
              <Trophy className="text-forest shrink-0" size={20}/>
              <div className="flex-1">
                <div className="font-bold text-forest">Caccia completata! 🏆</div>
                <div className="text-xs text-ink-700">Hai guadagnato {progress.points_earned} punti. Puoi rifarla quando vuoi.</div>
              </div>
            </div>
          )}

          <div className="pt-2 flex flex-col sm:flex-row gap-3">
            {needsPayment ? (
              <button onClick={buyRoute} disabled={busy} className="btn-tactile flex-1 flex items-center justify-center gap-2" data-testid="buy-route-btn">
                <CreditCard size={20}/> {busy ? "Attendi..." : `Acquista €${route.price_eur.toFixed(2)}`}
              </button>
            ) : progress?.status === "completed" ? (
              <button onClick={restartRoute} disabled={busy} className="btn-tactile flex-1 flex items-center justify-center gap-2" data-testid="restart-route-button">
                <RotateCcw size={20}/> {busy ? "Avvio..." : "Rifai la caccia"}
              </button>
            ) : progress?.status === "in_progress" ? (
              <button onClick={() => navigate(`/play/${id}`)} className="btn-tactile flex-1 flex items-center justify-center gap-2" data-testid="continue-route-button">
                <Play size={20} fill="currentColor"/> Continua
              </button>
            ) : (
              <button onClick={startRoute} disabled={busy} className="btn-tactile flex-1 flex items-center justify-center gap-2" data-testid="start-route-button">
                <Play size={20} fill="currentColor"/> {busy ? "Avvio..." : "Inizia l'avventura"}
              </button>
            )}
            <button onClick={() => navigate(`/leaderboard?route=${id}`)} className="px-6 py-4 rounded-2xl bg-white border border-outline text-ink-800 font-bold flex items-center justify-center gap-2" data-testid="route-leaderboard-btn">
              <Sparkles size={18}/> Classifica
            </button>
          </div>

          {/* Admin-only: reset del proprio progresso (utile per testing / demo) */}
          {isAdmin && progress?.status === "in_progress" && (
            <div className="mt-2 pt-3 border-t border-outline">
              <div className="text-[10px] uppercase tracking-widest text-compass font-bold mb-2 flex items-center gap-1.5">
                <ShieldAlert size={12}/> Strumenti admin
              </div>
              <button
                onClick={restartRoute}
                disabled={busy}
                className="w-full px-4 py-3 rounded-2xl bg-white border-2 border-compass/50 text-compass font-bold flex items-center justify-center gap-2 hover:bg-compass hover:text-white transition-all disabled:opacity-50"
                data-testid="admin-reset-route-btn"
              >
                <RotateCcw size={16}/> Resetta caccia e riparti dall'inizio
              </button>
              <p className="text-[11px] text-ink-500 mt-1.5 italic">
                Solo per admin. Il progresso attuale verrà archiviato e riparti dalla Tappa 1.
              </p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}